import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Chip
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrash,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import PartDialog from './components/PartDialog';
const { rootUrl } = require('../../../config/config');

const JobCardParts = ({ jobCardId, serviceLocation, onUpdate }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [parts, setParts] = useState([]);
    const [showAddPart, setShowAddPart] = useState(false);
    const [editingPart, setEditingPart] = useState(null);

    useEffect(() => {
        if (jobCardId) {
            fetchParts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobCardId]);

    const fetchParts = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/api/job-cards/${jobCardId}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success && response.data.data.parts) {
                setParts(response.data.data.parts);
            }
        } catch (error) {
            console.error('Error fetching parts:', error);
        }
    };

    const handleDeletePart = async (partId) => {
        if (!window.confirm('Are you sure you want to remove this part?')) {
            return;
        }

        try {
            const response = await axios.delete(
                `${rootUrl}/api/job-cards/${jobCardId}/parts/${partId}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                fetchParts();
                onUpdate && onUpdate();
            } else {
                alert(response.data?.message || 'Failed to remove part');
            }
        } catch (error) {
            console.error('Error removing part:', error);
            alert(error.response?.data?.message || 'Failed to remove part');
        }
    };

    const handleEditClick = (part) => {
        setEditingPart(part);
    };

    const handlePartAdded = () => {
        fetchParts();
        onUpdate && onUpdate();
    };

    const handleCloseDialog = () => {
        setShowAddPart(false);
        setEditingPart(null);
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-0">
                <Typography variant="h6">Parts Used</Typography>
                <Button
                    size="sm"
                    color="green"
                    onClick={() => setShowAddPart(true)}
                    className="w-full sm:w-auto"
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add Part
                </Button>
            </div>

            {parts.length === 0 ? (
                <Card>
                    <CardBody className="text-center py-8">
                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-gray-400 mb-2" />
                        <Typography variant="paragraph" className="text-gray-600">
                            No parts added yet
                        </Typography>
                    </CardBody>
                </Card>
            ) : (
                <Card>
                    <CardBody className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-max table-auto text-left">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="p-3 md:p-4 border-b border-gray-300 text-xs md:text-sm">Part Name</th>
                                        <th className="p-3 md:p-4 border-b border-gray-300 text-xs md:text-sm">Quantity</th>
                                        <th className="p-3 md:p-4 border-b border-gray-300 text-xs md:text-sm">Unit Cost</th>
                                        <th className="p-3 md:p-4 border-b border-gray-300 text-xs md:text-sm">Total Cost</th>
                                        <th className="p-3 md:p-4 border-b border-gray-300 text-xs md:text-sm">Paid By</th>
                                        <th className="p-3 md:p-4 border-b border-gray-300 text-xs md:text-sm">Sale</th>
                                        <th className="p-3 md:p-4 border-b border-gray-300 text-xs md:text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parts.map((part) => (
                                        <tr key={part.id} className="hover:bg-gray-50">
                                            <td className="p-3 md:p-4 border-b border-gray-200">
                                                <Typography variant="small" className="font-semibold text-xs md:text-sm">
                                                    {part.part_name || part.part_id}
                                                </Typography>
                                            </td>
                                            <td className="p-3 md:p-4 border-b border-gray-200">
                                                <Typography variant="small" className="text-xs md:text-sm">{part.quantity_used}</Typography>
                                            </td>
                                            <td className="p-3 md:p-4 border-b border-gray-200">
                                                <Typography variant="small" className="text-xs md:text-sm">{formatCurrency(part.unit_cost)}</Typography>
                                            </td>
                                            <td className="p-3 md:p-4 border-b border-gray-200">
                                                <Typography variant="small" className="font-semibold text-xs md:text-sm">
                                                    {formatCurrency(part.total_cost)}
                                                </Typography>
                                            </td>
                                            <td className="p-3 md:p-4 border-b border-gray-200">
                                                <Typography variant="small" className="text-xs md:text-sm">{part.paid_by || '-'}</Typography>
                                            </td>
                                            <td className="p-3 md:p-4 border-b border-gray-200">
                                                {part.is_sale && (
                                                    <Chip value="Sale" color="green" size="sm" />
                                                )}
                                            </td>
                                            <td className="p-3 md:p-4 border-b border-gray-200">
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="text"
                                                        color="blue"
                                                        onClick={() => handleEditClick(part)}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="text"
                                                        color="red"
                                                        onClick={() => handleDeletePart(part.id)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                </Card>
            )}

            <PartDialog
                open={showAddPart || editingPart !== null}
                editingPart={editingPart}
                jobCardId={jobCardId}
                serviceLocation={serviceLocation}
                onClose={handleCloseDialog}
                onPartAdded={handlePartAdded}
            />
        </div>
    );
};

export default JobCardParts;

